import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CTASectionContent, CTAButton, CommunityPageSectionRow } from '@/types/cms';
import { Save, X, Plus, Trash2, ExternalLink } from 'lucide-react';

interface CTASectionEditorProps {
  section: CommunityPageSectionRow;
  onSave: (updates: Partial<CommunityPageSectionRow>) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const CTASectionEditor: React.FC<CTASectionEditorProps> = ({
  section,
  onSave,
  onCancel,
  isLoading = false
}) => {
  const content = section.content as CTASectionContent;
  
  const [formData, setFormData] = useState({
    title: section.title || '',
    subtitle: content.subtitle || '',
    background_type: content.background_type || 'primary_card',
    background_image: content.background_image || '',
    background_color: content.background_color || '',
    buttons: content.buttons || []
  });

  useEffect(() => {
    setFormData({
      title: section.title || '',
      subtitle: content.subtitle || '',
      background_type: content.background_type || 'primary_card',
      background_image: content.background_image || '',
      background_color: content.background_color || '',
      buttons: content.buttons || []
    });
  }, [section, content]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleButtonChange = (index: number, field: string, value: string) => {
    const updatedButtons = [...formData.buttons];
    updatedButtons[index] = { ...updatedButtons[index], [field]: value };
    setFormData(prev => ({ ...prev, buttons: updatedButtons }));
  };

  const addButton = () => {
    const newButton: CTAButton = {
      text: '',
      link: '',
      variant: 'primary'
    };
    setFormData(prev => ({ 
      ...prev, 
      buttons: [...prev.buttons, newButton]
    }));
  };

  const removeButton = (index: number) => {
    const updatedButtons = formData.buttons.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, buttons: updatedButtons }));
  };

  const handleSave = () => {
    const { title, ...contentFields } = formData;
    onSave({
      title: title,
      content: {
        ...content,
        ...contentFields
      }
    });
  };

  return (
    <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-medium">Edit CTA Section</h4>
        <div className="flex gap-2">
          <Button size="sm" onClick={handleSave} disabled={isLoading}>
            <Save className="h-4 w-4 mr-2" />
            Save
          </Button>
          <Button variant="outline" size="sm" onClick={onCancel}>
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {/* Title */}
        <div className="space-y-2">
          <Label htmlFor="cta-title">Main Title</Label>
          <Input
            id="cta-title"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            placeholder="Enter main call-to-action title"
          />
        </div>

        {/* Subtitle */}
        <div className="space-y-2">
          <Label htmlFor="cta-subtitle">Subtitle</Label>
          <Textarea
            id="cta-subtitle"
            value={formData.subtitle}
            onChange={(e) => handleInputChange('subtitle', e.target.value)}
            placeholder="Enter supporting text or description"
            rows={2}
          />
        </div>

        {/* Background Settings */}
        <div className="space-y-4 pt-4 border-t">
          <Label>Background Settings</Label>
          
          <div className="space-y-2">
            <Label htmlFor="cta-background-type">Background Type</Label>
            <Select
              value={formData.background_type}
              onValueChange={(value) => handleInputChange('background_type', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="minimal">Minimal</SelectItem>
                <SelectItem value="primary_card">Primary Card (Recommended)</SelectItem>
                <SelectItem value="gradient">Gradient</SelectItem>
                <SelectItem value="image">Image</SelectItem>
                <SelectItem value="color">Solid Color</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Conditional background fields */}
          {formData.background_type === 'image' && (
            <div className="space-y-2">
              <Label htmlFor="cta-background-image">Background Image URL</Label>
              <Input
                id="cta-background-image"
                value={formData.background_image}
                onChange={(e) => handleInputChange('background_image', e.target.value)}
                placeholder="Enter image URL"
              />
            </div>
          )}

          {formData.background_type === 'color' && (
            <div className="space-y-2">
              <Label htmlFor="cta-background-color">Background Color</Label>
              <div className="flex gap-2">
                <Input
                  id="cta-background-color"
                  value={formData.background_color}
                  onChange={(e) => handleInputChange('background_color', e.target.value)}
                  placeholder="e.g., #ffffff"
                />
                <input
                  type="color"
                  value={formData.background_color || '#ffffff'}
                  onChange={(e) => handleInputChange('background_color', e.target.value)}
                  className="w-12 h-10 rounded border border-input"
                />
              </div>
            </div>
          )}
        </div>

        {/* CTA Buttons */}
        <div className="space-y-4 pt-4 border-t">
          <div className="flex items-center justify-between">
            <Label>Call-to-Action Buttons</Label>
            <Button variant="outline" size="sm" onClick={addButton}>
              <Plus className="h-4 w-4 mr-2" />
              Add Button
            </Button>
          </div>

          <div className="space-y-3 max-h-48 overflow-y-auto">
            {formData.buttons.map((button, index) => (
              <Card key={index} className="border border-gray-200">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm">Button {index + 1}</CardTitle>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => removeButton(index)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Button Text</Label>
                      <Input
                        value={button.text || ''}
                        onChange={(e) => handleButtonChange(index, 'text', e.target.value)}
                        placeholder="e.g., Get Started"
                        className="text-sm"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Button Style</Label>
                      <Select
                        value={button.variant || 'primary'}
                        onValueChange={(value) => handleButtonChange(index, 'variant', value)}
                      >
                        <SelectTrigger className="text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="primary">Primary</SelectItem>
                          <SelectItem value="secondary">Secondary</SelectItem>
                          <SelectItem value="outline">Outline</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="space-y-1">
                    <Label className="text-xs flex items-center gap-1">
                      Link URL
                      {button.link && (button.link.startsWith('http') || button.link.startsWith('//')) && (
                        <ExternalLink className="h-3 w-3" />
                      )}
                    </Label>
                    <Input
                      value={button.link || ''}
                      onChange={(e) => handleButtonChange(index, 'link', e.target.value)}
                      placeholder="/services or https://example.com"
                      className="text-sm"
                    />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {formData.buttons.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <p>No buttons added yet. Click "Add Button" to create your first CTA button.</p>
            </div>
          )}
        </div>

        {/* Preview */}
        <div className="space-y-2 pt-4 border-t">
          <Label>Preview</Label>
          <div className="p-6 bg-white border rounded-lg">
            <div className="text-center space-y-4">
              {formData.title && (
                <h3 className="text-xl font-bold">{formData.title}</h3>
              )}
              {formData.subtitle && (
                <p className="text-muted-foreground">{formData.subtitle}</p>
              )}
              <div className="flex flex-wrap gap-2 justify-center">
                {formData.buttons.map((button, index) => (
                  <div
                    key={index}
                    className={`px-4 py-2 rounded text-sm font-medium ${
                      button.variant === 'primary'
                        ? 'bg-primary text-primary-foreground'
                        : button.variant === 'secondary'
                        ? 'bg-secondary text-secondary-foreground'
                        : 'border border-input bg-background hover:bg-accent'
                    }`}
                  >
                    {button.text || 'Button'}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CTASectionEditor;